import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDrop } from 'react-dnd';
import { v4 as uuidv4 } from 'uuid';
import ComponentRenderer from './ComponentRenderer';
import { COMPONENT_TYPES } from '../constants';
import { selectComponent } from '../redux/editorSlice';
import './TabLayoutComponent.css';

const TabLayoutComponent = ({ id, componentProps = {}, style = {}, isPreview = false, onTabChange = null, onUpdate = null }) => {
  const dispatch = useDispatch();
  
  // 컴포넌트 기본 속성 설정
  const {
    className = "tab-layout-wrapper",
    tabs = [
      { id: 'tab1', title: '탭 1', components: [] },
      { id: 'tab2', title: '탭 2', components: [] },
      { id: 'tab3', title: '탭 3', components: [] }
    ],
    activeTabId = 'tab1',
    is_logged = true,
    is_not_logged = true,
    tabStyle = {
      backgroundColor: '#f8f8f8',
      color: '#4a4a4a',
      activeBackgroundColor: '#ffffff',
      activeColor: '#ff4400',
      borderColor: '#e0e0e0',
      borderRadius: '0',
      fontSize: '15px',
      padding: '14px 20px'
    }
  } = componentProps || {};

  // 로그인 상태 확인
  const isUserLoggedIn = useSelector(state => state.withcookie?.isUserLoggedIn || false);
  const isAdminLoggedIn = useSelector(state => state.withcookie?.isAdminLoggedIn || false);
  
  // 선택된 컴포넌트 ID
  const selectedComponentId = useSelector(state => state.editor?.selectedComponentId);
  
  // 로그인 상태에 따른 표시 여부 결정
  const shouldRender = (isUserLoggedIn && is_logged) || (!isUserLoggedIn && is_not_logged) || isAdminLoggedIn || isPreview;
  
  // 초기 마운트 여부 추적을 위한 ref
  const isInitialMount = useRef(true);
  
  // 이전 탭 변경 사항 기록을 위한 ref
  const prevTabsRef = useRef(tabs);
  const prevActiveTabIdRef = useRef(activeTabId);
  
  // 현재 활성화된 탭 상태 관리
  const [currentTabId, setCurrentTabId] = useState(activeTabId);
  
  // 각 탭의 컴포넌트 목록 관리
  const [tabComponents, setTabComponents] = useState(tabs);
  
  // props 변경에 따른 상태 업데이트 - 모든 props 변경을 한 번에 처리
  useEffect(() => {
    // 활성 탭 ID가 변경된 경우 업데이트
    if (activeTabId !== prevActiveTabIdRef.current) {
      setCurrentTabId(activeTabId);
      prevActiveTabIdRef.current = activeTabId;
    }
    
    // 실제 탭 구조가 변경된 경우만 업데이트
    const hasTabsChanged = JSON.stringify(tabs) !== JSON.stringify(prevTabsRef.current);
    if (hasTabsChanged) {
      setTabComponents(tabs);
      prevTabsRef.current = tabs;
    }
  }, [activeTabId, tabs]);
  
  // 활성 탭 객체 가져오기 - 더 명확하게 기본값 처리
  const currentTab = useMemo(() => {
    // 현재 활성 탭 ID로 탭 찾기
    const tab = tabComponents.find(tab => tab.id === currentTabId);
    
    // 찾은 탭이 있으면 반환, 없으면 첫 번째 탭, 첫 번째 탭도 없으면 빈 탭 반환
    if (tab) return tab;
    if (tabComponents.length > 0) return tabComponents[0];
    return { id: 'empty', title: '빈 탭', components: [] };
  }, [currentTabId, tabComponents]);
  
  // 탭 변경 핸들러 - 더 명확하게 구현
  const handleTabChange = useCallback((tabId) => {
    if (tabId === currentTabId) return; // 이미 선택된 탭이면 무시
    
    setCurrentTabId(tabId);
    
    // 부모 컴포넌트에 탭 변경 알림
    if (onTabChange) {
      onTabChange(tabId);
    }
  }, [currentTabId, onTabChange]);
  
  // 컴포넌트 추가 핸들러
  const handleAddComponent = useCallback((tabId, newComponent) => {
    // 함수형 업데이트를 사용하여 이전 상태에 기반한 업데이트 수행
    setTabComponents(prevTabs => {
      // 이전 탭 컴포넌트 목록에서 업데이트 수행
      const updatedTabs = prevTabs.map(tab => {
        if (tab.id === tabId) {
          return {
            ...tab,
            components: [...(tab.components || []), newComponent]
          };
        }
        return tab;
      });
      
      // 부모 컴포넌트에 변경 사항 알림
      // 무한 루프 방지를 위해 setTimeout으로 콜백 지연 실행
      if (onUpdate) {
        setTimeout(() => {
          onUpdate(updatedTabs);
        }, 0);
      }
      
      return updatedTabs;
    });
  }, [onUpdate]);
  
  // 내부 컴포넌트 선택 핸들러
  const handleComponentSelect = useCallback((componentId) => {
    // Redux 액션 디스패치하여 선택된 컴포넌트 업데이트
    dispatch(selectComponent(componentId));
  }, [dispatch]);
  
  // 드롭 핸들러 - 현재 활성화된 탭
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: "COMPONENT",
    drop: (item, monitor) => {
      if (monitor.didDrop()) {
        return; // 이미 하위 요소에서 처리된 경우 중복 처리 방지
      }
      
      // 컴포넌트 타입에 따라 고유 ID 생성
      const componentId = `${item.type.toLowerCase()}-${Date.now()}`;
      
      // 드롭된 컴포넌트에 대한 초기 데이터 설정
      let initialData = {};
      let initialStyle = {};
      
      // 컴포넌트 타입에 따른 초기 데이터 설정
      if (item.type === COMPONENT_TYPES.FAQ) {
        initialData = {
          faqItems: [
            { question: '질문 1', answer: '답변 1' },
            { question: '질문 2', answer: '답변 2' }
          ]
        };
      } else if (item.type === COMPONENT_TYPES.RICH_EDITOR) {
        initialData = {
          content: '<p>내용을 입력하세요.</p>'
        };
      } else if (item.type === COMPONENT_TYPES.BUTTON) {
        initialData = {
          content: '버튼'
        };
        initialStyle = {
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          padding: '8px 16px',
          fontSize: '14px',
          width: '100%',
          height: '40px'
        };
      } else if (item.type === COMPONENT_TYPES.TEXT) {
        initialData = {
          content: '텍스트를 입력하세요'
        };
        initialStyle = {
          fontSize: '16px',
          color: '#333',
          padding: '10px'
        };
      } else if (item.type === COMPONENT_TYPES.IMAGE) {
        initialData = {
          src: 'https://via.placeholder.com/300x200',
          alt: '이미지 설명'
        };
        initialStyle = {
          width: '100%',
          height: 'auto',
          maxWidth: '100%'
        };
      }
      
      // 새 컴포넌트 생성
      const newComponent = {
        id: componentId,
        type: item.type,
        content: item.name || "",
        style: initialStyle,
        ...initialData
      };
      
      
      // 현재 활성 탭에 컴포넌트 추가
      handleAddComponent(currentTabId, newComponent);
      
      // 새로 추가된 컴포넌트 선택
      setTimeout(() => {
        handleComponentSelect(componentId);
      }, 100);
      
      return { tabId: currentTabId };
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver({ shallow: true }),
      canDrop: !!monitor.canDrop(),
    }),
  }), [currentTabId, handleAddComponent, handleComponentSelect]);
  
  // 컴포넌트를 표시하지 않아야 하는 경우 null 반환
  if (!shouldRender && !isPreview) {
    return null;
  }
  
  // 탭 스타일 생성 함수
  const getTabStyle = (isActive) => {
    return {
      backgroundColor: isActive ? tabStyle.activeBackgroundColor : tabStyle.backgroundColor,
      color: isActive ? tabStyle.activeColor : tabStyle.color,
      borderColor: tabStyle.borderColor,
      borderRadius: tabStyle.borderRadius,
      fontSize: tabStyle.fontSize,
      padding: tabStyle.padding,
      width: '100%',
      textAlign: 'left',
      marginBottom: '8px',
      borderLeft: isActive ? '4px solid ' + tabStyle.activeColor : '4px solid transparent',
      fontWeight: isActive ? '500' : '400'
    };
  };
  
  // 드롭 영역 스타일
  const getDropZoneStyle = () => {
    return {
      outline: isOver ? `2px dashed ${tabStyle.activeColor}` : 'none',
      backgroundColor: isOver ? 'rgba(255, 68, 0, 0.05)' : 'transparent',
      transition: 'all 0.2s ease'
    };
  };
  
  return (
    <div className={className} id={id} style={style}>
      {/* 디버그 정보 추가 */}
      
      <div className="tab-layout-container">
        {/* 좌우 2분할 레이아웃으로 변경 */}
        <div className="tab-layout-horizontal">
          {/* 왼쪽 탭 메뉴 영역 */}
          <div className="tab-sidebar">
            {tabComponents.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${currentTabId === tab.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleTabChange(tab.id);
                }}
                style={getTabStyle(currentTabId === tab.id)}
              >
                {tab.title} {currentTabId === tab.id ? '(활성)' : ''}
              </button>
            ))}
          </div>
          
          {/* 오른쪽 탭 콘텐츠 영역 - 드래그 앤 드롭으로 컴포넌트 추가 가능 */}
          <div 
            ref={drop} 
            className={`tab-content ${isOver ? 'drag-over' : ''}`}
            style={getDropZoneStyle()}
          >
            {isOver && (
              <div className="drop-indicator">
                컴포넌트를 드롭하세요
              </div>
            )}
            <div className="tab-pane active">
              {currentTab.components && currentTab.components.length > 0 ? (
                // 현재 탭에 컴포넌트가 있으면 렌더링
                currentTab.components.map((component) => (
                  <ComponentRenderer
                    key={component.id}
                    component={component}
                    isPreview={isPreview}
                    isSelected={component.id === selectedComponentId}
                    onSelect={() => handleComponentSelect(component.id)}
                  />
                ))
              ) : (
                // 컴포넌트가 없으면 드롭 영역 표시
                <div className="tab-empty-state">
                  {isPreview ? (
                    <p className="empty-message">
                      이 탭에는 아직 컴포넌트가 없습니다. 컴포넌트를 드래그하여 추가하세요.
                    </p>
                  ) : (
                    <p className="empty-message">
                      {currentTab.title} 탭 영역에 컴포넌트를 드래그하여 추가하세요.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabLayoutComponent; 